import { Inject, Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Operation, OperationObserverService } from '@x/common/operation';
import { AddressFormBottomSheetComponent } from '@x/ecommerce-shop/app/core/address/components/address-form-bottom-sheet/address-form-bottom-sheet.component';
import { AddressRegionNoMatchDialogComponent } from '@x/ecommerce-shop/app/core/address/components/address-region-no-match-dialog/address-region-no-match-dialog.component';
import { AddressViewDialogComponent } from '@x/ecommerce-shop/app/core/address/components/address-view-dialog/address-view-dialog.component';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import {
  IShopAddress,
  IShopAddressInput,
  IShopAuthUser,
  IShopUserAddress,
  ShopRegionService,
} from '@x/ecommerce/shop-client';
import { GeocodeService, IExpandedAddress, ILocateAddressResult } from '@x/geocode/client';
import { PointInput } from '@x/schemas/ecommerce';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface AddressBottomSheetData {
  address?: IShopAddressInput;
  sheetTitle?: string;
  user: Partial<IShopAuthUser>;
  defaultCoordinates: PointInput;
  defaultCountry?: string;
  origin: 'My Account' | 'Checkout';
}

export interface AddressViewDialogData {
  address: IShopAddress;
}

@Injectable()
export class AddressService {
  constructor(
    private _bottomSheet: MatBottomSheet,
    private operations: OperationObserverService,
    private shopRegionService: ShopRegionService,
    private dialog: MatDialog,
    private geocodeService: GeocodeService,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
  ) {}

  getAddressInputFromShopAddress(address: IShopUserAddress & IShopAddress): IShopAddressInput {
    const {
      id,
      __typename,
      hash,
      isDefaultBillingAddress,
      isDefaultShippingAddress,
      recipientHash,
      region,
      ...updateAddress
    } = address;
    return updateAddress;
  }

  getAddressInputFromExpandedAddress(address: IExpandedAddress): IShopAddressInput {
    return {
      street: address.street,
      suburb: address.suburb,
      city: address.city,
      postalCode: address.postcode,
      province: address.provinceCode,
      businessName: address.establishment,
      complex: address.complex,
      country: address.countryCode ?? this.shopConfig.defaultCountry.code,
      ...(address.lat &&
        address.lng && {
          coordinates: {
            lat: address.lat,
            lng: address.lng,
          },
        }),
    };
  }

  validAddress(
    input: AddressBottomSheetData,
    restrictedRegions: number[],
  ): Observable<Operation<IShopAddressInput | undefined>> {
    return this.operations.observe(
      this.openAddressBottomSheet({ ...input }).pipe(
        switchMap((data) => {
          if (restrictedRegions.length > 0 && data) {
            return this.shopRegionService.regionFromAddressInput({ address: data }).pipe(
              switchMap((regionMatch) => {
                if (regionMatch && restrictedRegions.includes(regionMatch.id)) return of(data);
                return this.openAddressNoRegionMatchDialog();
              }),
            );
          }

          return of(data);
        }),
      ),
    );
  }

  viewAddressDialog(data: AddressViewDialogData): Observable<any> {
    return this.dialog
      .open(AddressViewDialogComponent, {
        data,
        width: '500px',
        panelClass: 'fullwidth-dialog-mobile',
      })
      .afterClosed();
  }

  locateAddress(input: IShopAddressInput): Observable<ILocateAddressResult> {
    const { city, postalCode, country, suburb, province, street, complex, businessName } = input;

    return this.geocodeService.locateAddress({
      city,
      countryCode: country,
      postcode: postalCode,
      suburb,
      street,
      provinceCode: province,
      complex,
      establishment: businessName,
    });
  }

  private openAddressBottomSheet(
    data: AddressBottomSheetData,
  ): Observable<IShopAddressInput | undefined> {
    return this._bottomSheet
      .open(AddressFormBottomSheetComponent, {
        data,
        disableClose: true,
        panelClass: ['address-bottom-sheet'],
      })
      .afterDismissed();
  }

  private openAddressNoRegionMatchDialog(): Observable<undefined> {
    return this.dialog.open(AddressRegionNoMatchDialogComponent).afterClosed();
  }
}
